<template>
  <div class="assignments-page">

    <div class="page-header">
      <div>
        <h5 class="page-title">Training Assignments</h5>
        <p class="page-sub">Auto-enrol employees in courses based on audience rules</p>
      </div>
      <div style="display:flex;gap:8px;align-items:center;">
        <button class="btn-export" @click="openExportModal('csv')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='csv' ? 'spinner' : 'file-csv'" :spin="exportLoading && pendingExportFmt==='csv'" /> CSV
        </button>
        <button class="btn-export" @click="openExportModal('pdf')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='pdf' ? 'spinner' : 'file-pdf'" :spin="exportLoading && pendingExportFmt==='pdf'" /> PDF
        </button>
        <button class="btn-primary" @click="openModal()">
          <font-awesome-icon icon="plus" /> New Assignment
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <select v-model="filter.status" class="filter-select" @change="load(1)">
        <option value="">All statuses</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="load(1)">
          <option v-for="n in [5, 10, 20, 50, 100, 200, 500, 1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div v-if="loading" class="table-loading"><div class="spinner"></div></div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Course</th>
            <th>Type</th>
            <th>Due</th>
            <th>Audience</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!assignments.length">
            <td colspan="7" class="empty-row">No assignments found.</td>
          </tr>
          <tr v-for="a in assignments" :key="a.id">
            <td class="td-name">{{ a.name }}</td>
            <td class="td-course">
              <span v-if="a.course">{{ a.course.title }}</span>
              <span v-else class="muted">—</span>
            </td>
            <td><span :class="['badge', 'badge--' + a.assignment_type]">{{ a.assignment_type }}</span></td>
            <td class="td-due">
              <span v-if="a.due_mode === 'fixed' && a.due_date">{{ formatDate(a.due_date) }}</span>
              <span v-else-if="a.due_mode === 'relative' && a.due_days">{{ a.due_days }} days</span>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <span v-if="a.rules && a.rules.length" class="rules-pill">
                {{ a.rules.length }} rule{{ a.rules.length !== 1 ? 's' : '' }}
              </span>
              <span v-else class="muted">—</span>
            </td>
            <td><span :class="['badge', a.status === 'active' ? 'badge--active' : 'badge--inactive']">{{ a.status }}</span></td>
            <td class="td-actions">
              <button class="action-btn" title="Edit" @click="openModal(a)"><font-awesome-icon icon="pen" /></button>
              <button v-if="a.status === 'active'" class="action-btn action-btn--danger" title="Deactivate" @click="confirmDeactivate(a)">
                <font-awesome-icon icon="ban" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="pagination.lastPage > 1" class="pagination-bar">
        <span class="pag-count">
          {{ ((pagination.currentPage - 1) * length) + 1 }}–{{ Math.min(pagination.currentPage * length, pagination.total) }} of {{ pagination.total }}
        </span>
        <div class="pag-btns">
          <button class="pag-btn" :disabled="pagination.currentPage <= 1" @click="load(1)" title="First">«</button>
          <button class="pag-btn" :disabled="pagination.currentPage <= 1" @click="load(pagination.currentPage - 1)">‹</button>
          <button
            v-for="p in pageRange"
            :key="p"
            :class="['pag-btn', p === pagination.currentPage ? 'pag-btn--active' : '']"
            @click="load(p)"
          >{{ p }}</button>
          <button class="pag-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="load(pagination.currentPage + 1)">›</button>
          <button class="pag-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="load(pagination.lastPage)" title="Last">»</button>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit Modal ──────────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="modal.show" class="modal-backdrop" @click.self="modal.show = false">
        <div class="modal-box">
          <div class="modal-head">
            <span class="modal-title">{{ modal.editId ? 'Edit Assignment' : 'New Assignment' }}</span>
            <button class="modal-close" @click="modal.show = false"><font-awesome-icon icon="times" /></button>
          </div>

          <div class="modal-body-inner">

            <!-- General -->
            <div class="form-section">
              <div class="fs-title">General</div>
              <div class="field-grid">
                <div class="field-group field-group--full">
                  <label class="field-label">Assignment Name <span class="req">*</span></label>
                  <input v-model="form.name" type="text" class="field-input" placeholder="e.g. Health & Safety – All Staff Q1" />
                </div>
                <div class="field-group" :class="{ 'field-group--full': modal.editId }">
                  <label class="field-label">Course <span v-if="!modal.editId" class="req">*</span></label>
                  <select v-if="!modal.editId" v-model="form.course_id" class="field-input">
                    <option value="">Select a course</option>
                    <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.title }}</option>
                  </select>
                  <div v-else class="field-input field-readonly">{{ modal.courseName }}</div>
                </div>
                <div v-if="!modal.editId" class="field-group">
                  <label class="field-label">Assignment Type <span class="req">*</span></label>
                  <select v-model="form.assignment_type" class="field-input">
                    <option value="">Select type</option>
                    <option value="mandatory">Mandatory</option>
                    <option value="recommended">Recommended</option>
                    <option value="optional">Optional</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Due Date -->
            <div class="form-section">
              <div class="fs-title">Due Date</div>
              <div class="field-grid">
                <div class="field-group">
                  <label class="field-label">Due Mode <span class="req">*</span></label>
                  <select v-model="form.due_mode" class="field-input">
                    <option value="relative">Relative (days from enrolment)</option>
                    <option value="fixed">Fixed date</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">
                    {{ form.due_mode === 'fixed' ? 'Due Date' : 'Days to Complete' }} <span class="req">*</span>
                  </label>
                  <input v-if="form.due_mode === 'fixed'" v-model="form.due_date" type="date" class="field-input" />
                  <input v-else v-model.number="form.due_days" type="number" min="1" class="field-input" placeholder="e.g. 30" />
                </div>
              </div>
            </div>

            <!-- Audience Rules -->
            <div class="form-section">
              <div class="fs-title-row">
                <span class="fs-title">Audience Rules</span>
                <button type="button" class="btn-add-rule" @click="addRule">
                  <font-awesome-icon icon="plus" /> Add Rule
                </button>
              </div>
              <p class="rule-hint">Target who gets enrolled. Multiple rules use AND logic.</p>

              <div v-if="!form.rules.length" class="rules-empty">
                No rules yet. Click "Add Rule" to target specific groups or all employees.
              </div>

              <div v-for="(rule, idx) in form.rules" :key="rule._id" class="rule-row">
                <!-- Criterion -->
                <div class="rule-cell rule-cell--crit">
                  <select v-model="rule.criterion" class="field-input field-input--sm" @change="onCriterionChange(rule)">
                    <option value="all">All Employees</option>
                    <option value="department">Department</option>
                    <option value="unit">Unit / Team</option>
                    <option value="position">Job Position</option>
                    <option value="grade">Pay Grade</option>
                    <option value="location">Location</option>
                    <option value="emp_type">Employment Type</option>
                    <option value="employee">Specific Employee</option>
                  </select>
                </div>

                <!-- Operator (hidden for 'all') -->
                <div v-if="rule.criterion !== 'all'" class="rule-cell rule-cell--op">
                  <select v-model="rule.operator" class="field-input field-input--sm">
                    <option value="include">Include</option>
                    <option value="exclude">Exclude</option>
                  </select>
                </div>

                <!-- Multi-select values (hidden for 'all') -->
                <div v-if="rule.criterion !== 'all'" class="rule-cell rule-cell--val">
                  <div class="ms-wrap" :ref="'ms-' + rule._id">
                    <!-- Trigger box -->
                    <div
                      class="ms-trigger"
                      :class="{ 'ms-trigger--open': rule._open }"
                      @click="toggleDropdown(rule)"
                    >
                      <div class="chips-area">
                        <span v-if="!rule.values.length" class="ms-placeholder">
                          Select {{ criterionLabel(rule.criterion) }}…
                        </span>
                        <span v-for="v in rule.values" :key="v.id" class="chip">
                          {{ v.name }}
                          <button type="button" class="chip-x" @click.stop="removeValue(rule, v)">×</button>
                        </span>
                      </div>
                      <font-awesome-icon icon="chevron-down" class="ms-arrow" :class="{ 'ms-arrow--up': rule._open }" />
                    </div>

                    <!-- Dropdown -->
                    <div v-if="rule._open" class="ms-dropdown">
                      <div class="ms-search-wrap">
                        <font-awesome-icon icon="search" class="ms-search-icon" />
                        <input
                          v-model="rule._search"
                          type="text"
                          class="ms-search-input"
                          :placeholder="'Search ' + criterionLabel(rule.criterion) + '…'"
                          @click.stop
                        />
                      </div>
                      <div class="ms-list">
                        <div v-if="!filteredOptions(rule).length" class="ms-empty">No results</div>
                        <label
                          v-for="opt in filteredOptions(rule)"
                          :key="opt.id"
                          class="ms-option"
                          @click.stop
                        >
                          <input
                            type="checkbox"
                            :checked="isSelected(rule, opt)"
                            @change="toggleValue(rule, opt)"
                            class="ms-checkbox"
                          />
                          <span class="ms-opt-label">{{ opt.name }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <button type="button" class="rule-remove-btn" @click="removeRule(idx)" title="Remove">
                  <font-awesome-icon icon="times" />
                </button>
              </div>

              <!-- Audience preview -->
              <div v-if="form.rules.length" class="preview-row">
                <button type="button" class="btn-preview" @click="previewAudience" :disabled="previewing">
                  <span v-if="previewing" class="spinner-sm spinner-sm--dark"></span>
                  <font-awesome-icon v-else icon="eye" />
                  Preview Audience
                </button>
                <span v-if="audienceCount !== null" class="audience-count">
                  ~{{ audienceCount }} employee{{ audienceCount !== 1 ? 's' : '' }} matched
                </span>
              </div>
            </div>

            <div v-if="modal.errorMsg" class="error-banner">
              <font-awesome-icon icon="exclamation-circle" /> {{ modal.errorMsg }}
            </div>

          </div>

          <div class="modal-foot">
            <button class="btn-outline" @click="modal.show = false">Cancel</button>
            <button class="btn-primary" @click="saveModal" :disabled="modal.saving">
              <span v-if="modal.saving" class="spinner-sm"></span>
              <font-awesome-icon v-else icon="check" />
              {{ modal.editId ? 'Save Changes' : 'Create Assignment' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Deactivate Confirm ───────────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="confirm.show" class="modal-backdrop" @click.self="confirm.show = false">
        <div class="confirm-box">
          <div class="confirm-icon-wrap">
            <font-awesome-icon icon="ban" />
          </div>
          <h6 class="confirm-title">Deactivate Assignment</h6>
          <p class="confirm-msg">Deactivate "{{ confirm.name }}"? New enrolments will stop. Existing enrolments are not affected.</p>
          <div class="confirm-actions">
            <button class="btn-outline" @click="confirm.show = false">Cancel</button>
            <button class="btn-deactivate" @click="doDeactivate" :disabled="confirm.loading">
              <span v-if="confirm.loading" class="spinner-sm"></span>
              Deactivate
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

let _ruleId = 0;
function makeRule(criterion = 'all') {
  return {
    _id: ++_ruleId,
    criterion,
    operator: 'include',
    values: [],
    _open: false,
    _search: '',
  };
}

export default {
  name: 'TrainingAssignments',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: false,
      assignments: [],
      courses: [],
      pagination: { currentPage: 1, lastPage: 1, total: 0 },
      length: 5,
      filter: { status: '' },
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportLoading: false,
      exportColumnDefs: [
        { key: 'name',     label: 'Name',     pdfWidth: 160 },
        { key: 'course',   label: 'Course',   pdfWidth: 185 },
        { key: 'type',     label: 'Type',     pdfWidth: 90 },
        { key: 'due',      label: 'Due',      pdfWidth: 106 },
        { key: 'audience', label: 'Audience', pdfWidth: 80 },
        { key: 'status',   label: 'Status',   pdfWidth: 80 },
      ],

      lookups: {
        department: [],
        unit:       [],
        position:   [],
        grade:      [],
        location:   [],
        employee:   [],
        emp_type: [
          { id: 'full_time', name: 'Full Time' },
          { id: 'part_time', name: 'Part Time' },
          { id: 'contract', name: 'Contract' },
          { id: 'intern', name: 'Intern' },
          { id: 'casual', name: 'Casual' },
        ],
      },

      modal: {
        show: false,
        editId: null,
        courseName: '',
        saving: false,
        errorMsg: '',
      },
      form: {
        name: '',
        course_id: '',
        assignment_type: '',
        due_mode: 'relative',
        due_date: '',
        due_days: 30,
        rules: [],
      },

      previewing: false,
      audienceCount: null,

      confirm: {
        show: false,
        id: null,
        name: '',
        loading: false,
      },
    };
  },
  computed: {
    pageRange() {
      const cur  = this.pagination.currentPage || 1;
      const last = this.pagination.lastPage    || 1;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },
  },
  created() {
    this.load();
    this.loadLookups();
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {

    // ── Export ────────────────────────────────────────────────────────────

    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(row, key) {
      switch (key) {
        case 'name':     return row.name || '—';
        case 'course':   return row.course?.title || '—';
        case 'type':     return row.assignment_type || '—';
        case 'due':
          if (row.due_mode === 'fixed' && row.due_date)        return this.formatDate(row.due_date);
          if (row.due_mode === 'relative' && row.due_days)     return row.due_days + ' days';
          return '—';
        case 'audience': return row.rules ? row.rules.length + ' rule' + (row.rules.length !== 1 ? 's' : '') : '—';
        case 'status':   return row.status || '—';
        default:         return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.filter.status) params.status = this.filter.status;
        const { data } = await axios.get('/training/assignments', { params });
        const d    = data.data;
        const rows = d.data ?? d ?? [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `training-assignments-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Training Assignments', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`training-assignments-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally   { this.exportLoading = false; }
    },

    // ── Data loading ──────────────────────────────────────────────────────

    async load(page = 1) {
      this.loading = true;
      try {
        const params = { page, per_page: this.length };
        if (this.filter.status) params.status = this.filter.status;
        const { data } = await axios.get('/training/assignments', { params });
        const res = data.data;
        this.assignments      = res.data ?? res;
        this.pagination = res.last_page
          ? { currentPage: res.current_page, lastPage: res.last_page, total: res.total ?? 0 }
          : { currentPage: 1, lastPage: 1, total: 0 };
      } catch (e) {
        if (e.response?.status !== 403) this.toast.error('Failed to load assignments.');
      } finally {
        this.loading = false;
      }
    },

    async loadLookups() {
      // Handles both paginated { data: { data: [] } } and simple { data: [] } shapes
      const extractList = (result) => {
        if (result.status !== 'fulfilled') return [];
        const d = result.value.data;
        if (Array.isArray(d?.data?.data)) return d.data.data;
        if (Array.isArray(d?.data))       return d.data;
        return [];
      };

      try {
        const [depts, units, positions, grades, locs, emps, courses] = await Promise.allSettled([
          axios.get('/payroll/department'),
          axios.get('/payroll/unit'),
          axios.get('/payroll/position/active'),
          axios.get('/payroll/paygrade/active'),
          axios.get('/admin/location/active'),
          axios.get('/payroll/employees/active', { params: { paginate: 0 } }),
          axios.get('/training/courses', { params: { status: 'published', per_page: 100 } }),
        ]);

        this.lookups.department = extractList(depts);
        this.lookups.unit       = extractList(units);
        this.lookups.position   = extractList(positions);
        this.lookups.grade      = extractList(grades);
        this.lookups.location   = extractList(locs);

        this.lookups.employee = extractList(emps).map(e => ({
          id:   e.id,
          name: `${e.first_name} ${e.last_name}${e.employee_no ? ' (' + e.employee_no + ')' : ''}`,
        }));

        // Courses list: paginated → data.data.data, or flat → data.data
        const rawCourses = extractList(courses);
        this.courses = rawCourses;
      } catch { /* silent */ }
    },

    // ── Dropdown helpers ──────────────────────────────────────────────────

    lookupList(criterion) {
      return this.lookups[criterion] ?? [];
    },

    criterionLabel(criterion) {
      return {
        department: 'department', unit: 'unit', position: 'position',
        grade: 'grade', location: 'location', emp_type: 'employment type',
        employee: 'employee',
      }[criterion] || criterion;
    },

    filteredOptions(rule) {
      const list = this.lookupList(rule.criterion);
      const q = rule._search.trim().toLowerCase();
      if (!q) return list;
      return list.filter(o => o.name.toLowerCase().includes(q));
    },

    isSelected(rule, opt) {
      return rule.values.some(v => String(v.id) === String(opt.id));
    },

    toggleValue(rule, opt) {
      if (this.isSelected(rule, opt)) {
        rule.values = rule.values.filter(v => String(v.id) !== String(opt.id));
      } else {
        rule.values.push({ id: opt.id, name: opt.name });
      }
      this.audienceCount = null;
    },

    removeValue(rule, val) {
      rule.values = rule.values.filter(v => String(v.id) !== String(val.id));
      this.audienceCount = null;
    },

    toggleDropdown(rule) {
      const wasOpen = rule._open;
      // Close all dropdowns first
      this.form.rules.forEach(r => { r._open = false; });
      if (!wasOpen) rule._open = true;
    },

    onDocClick(e) {
      // Close any open dropdown if click is outside a .ms-wrap
      if (!e.target.closest('.ms-wrap')) {
        this.form.rules.forEach(r => { r._open = false; });
      }
    },

    // ── Rules ─────────────────────────────────────────────────────────────

    addRule() {
      this.form.rules.push(makeRule('all'));
      this.audienceCount = null;
    },

    removeRule(idx) {
      this.form.rules.splice(idx, 1);
      this.audienceCount = null;
    },

    onCriterionChange(rule) {
      rule.values  = [];
      rule._search = '';
      rule._open   = false;
      rule.operator = 'include';
      this.audienceCount = null;
    },

    // Flatten UI rules → API rules (one rule per value)
    flattenRules() {
      const out = [];
      for (const rule of this.form.rules) {
        if (rule.criterion === 'all') {
          out.push({ criterion: 'all', operator: 'include', value_id: null, value_label: null });
        } else {
          for (const v of rule.values) {
            out.push({
              criterion:   rule.criterion,
              operator:    rule.operator,
              value_id:    v.id,
              value_label: v.name,
            });
          }
        }
      }
      return out;
    },

    // Group API rules → UI rules (one row per criterion+operator group)
    groupRules(apiRules) {
      const groups = new Map();
      for (const r of apiRules) {
        if (r.criterion === 'all') {
          groups.set('all', makeRule('all'));
          continue;
        }
        const key = `${r.criterion}|${r.operator ?? 'include'}`;
        if (!groups.has(key)) {
          const rule  = makeRule(r.criterion);
          rule.operator = r.operator ?? 'include';
          groups.set(key, rule);
        }
        if (r.value_id) {
          groups.get(key).values.push({ id: r.value_id, name: r.value_label ?? r.value_id });
        }
      }
      return Array.from(groups.values());
    },

    // ── Preview ───────────────────────────────────────────────────────────

    async previewAudience() {
      this.previewing  = true;
      this.audienceCount = null;
      try {
        const { data } = await axios.post('/training/assignments/preview-audience', {
          rules: this.flattenRules(),
        });
        const d = data.data;
        this.audienceCount = typeof d?.count === 'number' ? d.count
          : Array.isArray(d) ? d.length : null;
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Preview failed.');
      } finally {
        this.previewing = false;
      }
    },

    // ── Modal ─────────────────────────────────────────────────────────────

    openModal(assignment = null) {
      this.modal.errorMsg  = '';
      this.modal.editId    = assignment?.id ?? null;
      this.modal.courseName = assignment?.course?.title ?? '';
      this.audienceCount   = null;

      if (assignment) {
        this.form = {
          name:            assignment.name,
          course_id:       assignment.course_id ?? '',
          assignment_type: assignment.assignment_type,
          due_mode:        assignment.due_mode,
          due_date:        assignment.due_date ? assignment.due_date.substring(0, 10) : '',
          due_days:        assignment.due_days ?? 30,
          rules:           this.groupRules(assignment.rules ?? []),
        };
      } else {
        this.form = {
          name: '', course_id: '', assignment_type: '',
          due_mode: 'relative', due_date: '', due_days: 30,
          rules: [],
        };
      }
      this.modal.show = true;
    },

    async saveModal() {
      this.modal.errorMsg = '';

      if (!this.form.name.trim())           { this.modal.errorMsg = 'Assignment name is required.'; return; }
      if (!this.modal.editId && !this.form.course_id)         { this.modal.errorMsg = 'Please select a course.'; return; }
      if (!this.modal.editId && !this.form.assignment_type)   { this.modal.errorMsg = 'Please select an assignment type.'; return; }
      if (this.form.due_mode === 'fixed'    && !this.form.due_date)  { this.modal.errorMsg = 'Please set a due date.'; return; }
      if (this.form.due_mode === 'relative' && !this.form.due_days)  { this.modal.errorMsg = 'Please enter number of days.'; return; }
      if (!this.form.rules.length)          { this.modal.errorMsg = 'Add at least one audience rule.'; return; }

      const incomplete = this.form.rules.find(r => r.criterion !== 'all' && !r.values.length);
      if (incomplete) { this.modal.errorMsg = `Select at least one value for the "${this.criterionLabel(incomplete.criterion)}" rule.`; return; }

      const flatRules = this.flattenRules();

      this.modal.saving = true;
      try {
        const payload = {
          name:     this.form.name,
          due_mode: this.form.due_mode,
          due_date: this.form.due_mode === 'fixed'     ? this.form.due_date : null,
          due_days: this.form.due_mode === 'relative'  ? this.form.due_days : null,
          rules:    flatRules,
        };
        if (!this.modal.editId) {
          payload.course_id       = this.form.course_id;
          payload.assignment_type = this.form.assignment_type;
        }

        if (this.modal.editId) {
          await axios.put(`/training/assignments/${this.modal.editId}`, payload);
          this.toast.success('Assignment updated.');
        } else {
          await axios.post('/training/assignments', payload);
          this.toast.success('Assignment created. Enrolments are being processed.');
        }
        this.modal.show = false;
        this.load(1);
      } catch (e) {
        this.modal.errorMsg = e.response?.data?.message ?? 'An error occurred.';
      } finally {
        this.modal.saving = false;
      }
    },

    // ── Deactivate ────────────────────────────────────────────────────────

    confirmDeactivate(a) {
      this.confirm = { show: true, id: a.id, name: a.name, loading: false };
    },

    async doDeactivate() {
      this.confirm.loading = true;
      try {
        await axios.post(`/training/assignments/${this.confirm.id}/deactivate`);
        this.toast.success('Assignment deactivated.');
        this.confirm.show = false;
        this.load(this.pagination.currentPage);
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to deactivate.');
        this.confirm.loading = false;
      }
    },

    // ── Utils ─────────────────────────────────────────────────────────────

    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.assignments-page { padding-bottom: 40px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar { display:flex; gap:10px; margin-bottom:16px; }
.filter-select { padding:8px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Table ─────────────────────────────────────────────────────────────── */

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.table-loading { display:flex; align-items:center; justify-content:center; padding:60px; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table th { padding:10px 16px; text-align:left; font-size:11px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.05em; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table td { padding:12px 16px; border-bottom:1px solid #f0f4f8; vertical-align:middle; color:#1a2b45; }
.data-table tr:last-child td { border-bottom:none; }
.data-table tr:hover td { background:#fafcff; }
.empty-row { text-align:center; color:#a0b0c8; padding:40px !important; }
.td-name { font-weight:600; }
.td-course { max-width:200px; }
.td-due { white-space:nowrap; }
.muted { color:#a0b0c8; }
.rules-pill { display:inline-block; background:#eef2ff; color:#4060c8; font-size:11px; font-weight:600; padding:2px 8px; border-radius:10px; }

.badge { display:inline-block; padding:2px 8px; border-radius:5px; font-size:11px; font-weight:600; text-transform:capitalize; }
.badge--mandatory   { background:#fff0f0; color:#c03030; }
.badge--recommended { background:#fff8e6; color:#9a6500; }
.badge--optional    { background:#f0f8f0; color:#2a7a3a; }
.badge--active      { background:#e8f5e9; color:#2e7d32; }
.badge--inactive    { background:#f5f5f5; color:#9e9e9e; }

.td-actions { display:flex; gap:4px; align-items:center; }
.action-btn { width:30px; height:30px; border:none; background:transparent; color:#a0b0c8; border-radius:6px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:12px; }
.action-btn:hover { background:#f0f4f8; color:#1a2b45; }
.action-btn--danger:hover { background:#fff0f0; color:#e03535; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-top:1px solid #f0f4f8; flex-wrap:wrap; gap:8px; }
.pag-count { font-size:12px; color:#7a90b0; }
.pag-btns  { display:flex; gap:4px; }
.pag-btn   { min-width:30px; height:30px; border:1.5px solid #dde5f0; border-radius:6px; background:#fff; color:#1a2b45; font-size:12px; cursor:pointer; padding:0 6px; }
.pag-btn:hover:not(:disabled) { background:#f0f4f8; }
.pag-btn:disabled { opacity:.35; cursor:not-allowed; }
.pag-btn--active { background:#004AAD; color:#fff; border-color:#004AAD; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:12px; color:#7a90b0; }
.per-page-select { height:32px; border:1.5px solid #dde5f0; border-radius:7px; padding:0 8px; font-size:12px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }
.per-page-select:focus { border-color:#004AAD; }

/* ── Modal ─────────────────────────────────────────────────────────────── */

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:680px; box-shadow:0 20px 60px rgba(0,0,0,.18); margin:auto; }
.modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; position:sticky; top:0; background:#fff; z-index:1; border-radius:14px 14px 0 0; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; }
.modal-close { border:none; background:none; color:#a0b0c8; cursor:pointer; font-size:15px; padding:4px 8px; border-radius:6px; }
.modal-close:hover { background:#f4f7fb; color:#1a2b45; }
.modal-body-inner { padding:20px; }
.modal-foot { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding:14px 20px; border-top:1px solid #f0f4f8; position:sticky; bottom:0; background:#fff; border-radius:0 0 14px 14px; }

.form-section { margin-bottom:22px; padding-bottom:22px; border-bottom:1px solid #f4f7fb; }
.form-section:last-of-type { border-bottom:none; margin-bottom:0; padding-bottom:0; }
.fs-title { font-size:11px; font-weight:700; color:#a0b0c8; text-transform:uppercase; letter-spacing:.07em; margin-bottom:12px; }
.fs-title-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }

.field-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.field-group { display:flex; flex-direction:column; gap:6px; }
.field-group--full { grid-column:1/-1; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; box-sizing:border-box; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input--sm { padding:8px 10px; font-size:12px; }
.field-readonly { color:#7a90b0; background:#f8fafc; cursor:default; }

/* ── Audience Rules ───────────────────────────────────────────────────── */

.rule-hint { font-size:12px; color:#a0b0c8; margin:0 0 14px; }
.rules-empty { font-size:13px; color:#a0b0c8; padding:16px; background:#f8fafc; border-radius:8px; text-align:center; margin-bottom:10px; }

.btn-add-rule { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border:1.5px solid #c2d0e8; border-radius:7px; background:#f8fafc; color:#5a7aaa; font-size:12px; font-weight:600; cursor:pointer; }
.btn-add-rule:hover { border-color:#004AAD; color:#004AAD; background:#f0f4ff; }

.rule-row { display:flex; align-items:flex-start; gap:8px; margin-bottom:10px; flex-wrap:wrap; }
.rule-cell { display:flex; flex-direction:column; }
.rule-cell--crit { width:160px; flex-shrink:0; }
.rule-cell--op { width:110px; flex-shrink:0; }
.rule-cell--val { flex:1; min-width:200px; }

.rule-remove-btn { width:28px; height:34px; border:none; background:transparent; color:#c0c0d0; cursor:pointer; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; margin-top:1px; }
.rule-remove-btn:hover { background:#fff0f0; color:#e03535; }

/* ── Multi-select ─────────────────────────────────────────────────────── */

.ms-wrap { position:relative; width:100%; }

.ms-trigger { display:flex; align-items:center; gap:6px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; padding:6px 10px 6px 10px; cursor:pointer; min-height:36px; transition:border-color .15s; }
.ms-trigger:hover { border-color:#b0c0d8; }
.ms-trigger--open { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }

.chips-area { display:flex; flex-wrap:wrap; gap:4px; flex:1; min-width:0; }
.ms-placeholder { font-size:12px; color:#a0b0c8; padding:1px 0; }
.chip { display:inline-flex; align-items:center; gap:4px; background:#eef2ff; color:#3050b0; font-size:11px; font-weight:600; padding:2px 6px 2px 8px; border-radius:5px; }
.chip-x { border:none; background:none; color:#8090c0; cursor:pointer; font-size:14px; line-height:1; padding:0; display:flex; align-items:center; }
.chip-x:hover { color:#e03535; }

.ms-arrow { color:#a0b0c8; font-size:11px; flex-shrink:0; transition:transform .15s; }
.ms-arrow--up { transform:rotate(180deg); }

.ms-dropdown { position:absolute; top:calc(100% + 4px); left:0; right:0; background:#fff; border:1.5px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.12); z-index:200; overflow:hidden; }

.ms-search-wrap { display:flex; align-items:center; gap:8px; padding:8px 12px; border-bottom:1px solid #f0f4f8; }
.ms-search-icon { color:#a0b0c8; font-size:12px; flex-shrink:0; }
.ms-search-input { flex:1; border:none; outline:none; font-size:13px; color:#1a2b45; background:transparent; }
.ms-search-input::placeholder { color:#b0bcd0; }

.ms-list { max-height:200px; overflow-y:auto; }
.ms-empty { padding:16px; text-align:center; font-size:13px; color:#a0b0c8; }
.ms-option { display:flex; align-items:center; gap:10px; padding:8px 12px; cursor:pointer; transition:background .1s; }
.ms-option:hover { background:#f4f7fb; }
.ms-checkbox { width:14px; height:14px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.ms-opt-label { font-size:13px; color:#1a2b45; }

/* ── Preview ────────────────────────────────────────────────────────────── */

.preview-row { display:flex; align-items:center; gap:12px; margin-top:14px; }
.btn-preview { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; color:#5a7aaa; font-size:12px; font-weight:500; cursor:pointer; }
.btn-preview:hover:not(:disabled) { border-color:#004AAD; color:#004AAD; }
.btn-preview:disabled { opacity:.6; cursor:not-allowed; }
.audience-count { font-size:13px; font-weight:600; color:#2d8a3e; }

.error-banner { display:flex; align-items:center; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-top:12px; color:#e03535; font-size:13px; }

/* ── Confirm ────────────────────────────────────────────────────────────── */

.confirm-box { background:#fff; border-radius:16px; padding:28px; width:100%; max-width:380px; text-align:center; box-shadow:0 20px 60px rgba(0,0,0,.18); margin:auto; }
.confirm-icon-wrap { width:52px; height:52px; border-radius:50%; background:#fff3e0; color:#e07000; display:flex; align-items:center; justify-content:center; font-size:20px; margin:0 auto 14px; }
.confirm-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.confirm-msg { font-size:13px; color:#7a90b0; margin:0 0 20px; line-height:1.5; }
.confirm-actions { display:flex; gap:10px; justify-content:center; }
.btn-deactivate { display:inline-flex; align-items:center; gap:6px; background:#e07000; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; }
.btn-deactivate:hover:not(:disabled) { background:#c06000; }
.btn-deactivate:disabled { opacity:.6; cursor:not-allowed; }

/* ── Shared ─────────────────────────────────────────────────────────────── */

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#0056d2; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-export { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; white-space:nowrap; font-family:inherit; }
.btn-export:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.btn-export:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
.spinner-sm--dark { border:2px solid #c2d0e8; border-top-color:#004AAD; }
@keyframes spin { to { transform:rotate(360deg); } }

@media(max-width:640px) {
  .field-grid { grid-template-columns:1fr; }
  .field-group--full { grid-column:auto; }
  .rule-row { flex-direction:column; }
  .rule-cell--crit, .rule-cell--op { width:100%; }
}
</style>
